import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { EyeOff, Loader2, Search } from "lucide-react";

export default function HallazgosConfigTitulosTab() {
  const { getAuthHeaders, user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isAdmin = ["administrador", "admin", "superadmin"].includes((user?.role || "").toLowerCase());

  useEffect(() => {
    const load = async () => {
      try {
        const [uRes, cRes] = await Promise.all([
          axios.get(`${API}/tickets/usuarios-disponibles`, getAuthHeaders()),
          axios.get(`${API}/hallazgos-auditoria/config-titulos`, getAuthHeaders()),
        ]);
        setUsuarios(uRes.data || []);
        setSeleccion(cRes.data?.user_ids || []);
      } catch {
        toast.error("Error al cargar la configuración");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => {
    if (!isAdmin) return;
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const guardar = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/hallazgos-auditoria/config-titulos`, { user_ids: seleccion }, getAuthHeaders());
      toast.success("Usuarios autorizados actualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const filtrados = usuarios.filter(
    (u) => (u.name || "").toLowerCase().includes(busqueda.toLowerCase())
      || (u.email || "").toLowerCase().includes(busqueda.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="hallazgos-config-titulos-tab">
      <div className="mb-4 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
          <EyeOff className="w-4 h-4" /> Títulos privados de Quejas y Oportunidades
        </p>
        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
          Los usuarios seleccionados pueden ver el nombre del cliente (Queja de Cliente) y el nombre de la
          oportunidad (Oportunidad de Mejora). Los demás usuarios verán ese campo vacío. El administrador
          siempre puede verlos.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar usuario por nombre o correo..."
            className="pl-8 h-9"
            data-testid="config-titulos-buscar"
          />
        </div>
        <Badge variant="secondary" className="bg-slate-100 text-slate-600">{seleccion.length} autorizado(s)</Badge>
        {isAdmin && (
          <Button size="sm" onClick={guardar} disabled={saving} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="config-titulos-guardar">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Guardar
          </Button>
        )}
      </div>

      <div className="border rounded-lg divide-y dark:divide-slate-700 max-h-[26rem] overflow-y-auto bg-white dark:bg-slate-900">
        {filtrados.map((u) => {
          const activo = seleccion.includes(u.id);
          return (
            <button
              key={u.id}
              onClick={() => toggle(u.id)}
              disabled={!isAdmin}
              className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${activo ? "bg-emerald-50 dark:bg-emerald-900/20" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}
              data-testid={`config-titulos-user-${u.id}`}
            >
              <span>
                <span className="font-medium text-slate-800 dark:text-slate-100">{u.name}</span>
                <span className="text-xs text-slate-400 ml-2">{u.email}</span>
              </span>
              {activo && <Badge className="bg-emerald-600 text-white text-[10px]">Autorizado</Badge>}
            </button>
          );
        })}
        {filtrados.length === 0 && <p className="text-center text-sm text-slate-400 py-8">Sin usuarios</p>}
      </div>
    </div>
  );
}
